import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Upload, Plus, Trash2, FileText, Image as ImageIcon, Video, Megaphone, Download, Pencil,
} from "lucide-react";
import { StatusBadge, PaymentBadge, CampaignProgress, WhatsAppButton, Loading, EmptyState, Money } from "@/components/common";
import { formatDate, formatDateTime, pendingAmount } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { OrderFormDialog } from "./OrderForm";

const CAT_ICON = { logo: ImageIcon, creative: ImageIcon, video: Video, document: FileText, screenshot: ImageIcon };

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [files, setFiles] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, p, u, fl] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/orders/${orderId}/payments`),
        api.get(`/orders/${orderId}/updates`),
        api.get(`/orders/${orderId}/files`),
      ]);
      setOrder(o.data); setPayments(p.data); setUpdates(u.data); setFiles(fl.data);
    } catch (e) { setOrder(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (order === null) return <Loading />;
  if (!order) return <EmptyState title="Order not found" action={<Button onClick={() => navigate("/orders")}>Back to Orders</Button>} />;

  const pending = pendingAmount(order);

  const changeStatus = async (status) => {
    try { await api.post(`/orders/${orderId}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/orders")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to Orders
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900 font-mono">{order.order_id}</h1>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-slate-600 mt-1">{order.business_name} • {order.plan_name || "No plan"}</p>
        </div>
        <div className="flex items-center gap-2">
          <WhatsAppButton number={order.whatsapp || order.mobile} label="WhatsApp Customer" />
          {isAdmin && (
            <Button variant="outline" size="sm" className="h-9" onClick={() => setEditOpen(true)} data-testid="edit-order-button">
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          )}
          <Select value={order.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-40 bg-white" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["new", "confirmed", "in_progress", "running", "completed", "cancelled"].map((s) =>
                <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isAdmin && <OrderFormDialog open={editOpen} onOpenChange={setEditOpen} editOrder={order} onSaved={load} />}

      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <InfoCard label="Total Amount"><Money value={order.total_amount} /></InfoCard>
        <InfoCard label="Paid"><span className="text-emerald-700"><Money value={order.paid_amount} /></span></InfoCard>
        <InfoCard label="Pending"><span className="text-rose-700"><Money value={pending} /></span></InfoCard>
        <InfoCard label="Payment Status"><PaymentBadge status={order.payment_status} /></InfoCard>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign progress */}
          <Panel title="Campaign Progress" action={
            <ProgressEditor order={order} onSaved={load} />
          }>
            <CampaignProgress order={order} />
          </Panel>

          <Panel title="Order Details">
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Row k="Customer" v={order.business_name} />
              <Row k="Contact Person" v={order.contact_person} />
              <Row k="Mobile" v={order.mobile} />
              <Row k="WhatsApp" v={order.whatsapp} />
              <Row k="Email" v={order.email} />
              <Row k="Service / Plan" v={order.plan_name} />
              <Row k="Start Date" v={formatDate(order.start_date)} />
              <Row k="End Date" v={formatDate(order.end_date)} />
              <Row k="Assigned Staff" v={order.assigned_staff_name} />
              <Row k="Payment Method" v={order.payment_method} />
            </dl>
            {order.customer_requirements && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Customer Requirements</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.customer_requirements}</p>
              </div>
            )}
            {order.internal_notes && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs uppercase tracking-wider text-amber-600 mb-1">Internal Notes (staff only)</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap bg-amber-50 rounded-lg p-3 border border-amber-100">{order.internal_notes}</p>
              </div>
            )}
          </Panel>

          <Tabs defaultValue="updates">
            <TabsList>
              <TabsTrigger value="updates" data-testid="tab-updates">Updates</TabsTrigger>
              <TabsTrigger value="files" data-testid="tab-files">Files</TabsTrigger>
              <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            </TabsList>

            <TabsContent value="updates">
              <Panel title="Customer-visible Updates" action={<UpdateEditor orderId={orderId} onSaved={load} />}>
                {updates.length === 0 ? <p className="text-sm text-slate-500">No updates posted yet.</p> : (
                  <ol className="relative border-l border-slate-200 ml-1.5 space-y-5">
                    {updates.map((u) => (
                      <li key={u.id} className="ml-5">
                        <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-blue-600 ring-4 ring-white" />
                        <div className="text-sm font-semibold text-slate-900">{u.title}</div>
                        {u.message && <div className="text-sm text-slate-600 mt-0.5">{u.message}</div>}
                        <div className="text-xs text-slate-400 mt-1">{formatDateTime(u.created_at)} • {u.created_by_name}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </Panel>
            </TabsContent>

            <TabsContent value="files">
              <Panel title="Files" action={<FileUploader orderId={orderId} onSaved={load} />}>
                {files.length === 0 ? <p className="text-sm text-slate-500">No files uploaded.</p> : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {files.map((fl) => <FileRow key={fl.id} file={fl} isAdmin={isAdmin} onDelete={load} />)}
                  </div>
                )}
              </Panel>
            </TabsContent>

            <TabsContent value="payments">
              <Panel title="Payment History" action={isAdmin && <PaymentEditor orderId={orderId} onSaved={load} />}>
                {payments.length === 0 ? <p className="text-sm text-slate-500">No payments recorded.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Reference</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{formatDate(p.payment_date)}</TableCell>
                          <TableCell><Money value={p.amount} /></TableCell>
                          <TableCell>{p.payment_method || "—"}</TableCell>
                          <TableCell className="text-slate-500">{p.transaction_ref || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Panel>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar quick status timeline */}
        <div className="space-y-6">
          <Panel title="Status Timeline">
            <ol className="space-y-3">
              {["new", "confirmed", "in_progress", "running", "completed"].map((s, i) => {
                const order_flow = ["new", "confirmed", "in_progress", "running", "completed"];
                const done = order_flow.indexOf(order.status) >= i || order.status === "completed";
                const current = order.status === s;
                return (
                  <li key={s} className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${current ? "bg-blue-600 ring-4 ring-blue-100" : done ? "bg-emerald-500" : "bg-slate-200"}`} />
                    <span className={`text-sm capitalize ${current ? "font-semibold text-slate-900" : done ? "text-slate-700" : "text-slate-400"}`}>{s.replace("_", " ")}</span>
                  </li>
                );
              })}
            </ol>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1.5 text-lg font-bold font-display text-slate-900">{children}</div>
    </div>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
        <h3 className="text-base font-semibold font-display text-slate-900">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wider text-slate-400">{k}</dt>
      <dd className="text-slate-800 mt-0.5">{v || "—"}</dd>
    </div>
  );
}

function ProgressEditor({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState(order.generated_leads || 0);
  const save = async () => {
    try { await api.post(`/orders/${order.order_id}/progress`, { generated_leads: Number(leads) }); toast.success("Progress updated"); setOpen(false); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="edit-progress-button"><Plus className="h-3.5 w-3.5 mr-1" /> Update Leads</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Campaign Progress</DialogTitle></DialogHeader>
          <div><Label>Generated Leads</Label><Input data-testid="progress-input" type="number" value={leads} onChange={(e) => setLeads(e.target.value)} className="mt-1.5" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-progress" className="bg-[#1e3a8a]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UpdateEditor({ orderId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const save = async () => {
    if (!title) { toast.error("Title required"); return; }
    try { await api.post(`/orders/${orderId}/updates`, { title, message }); toast.success("Update posted"); setOpen(false); setTitle(""); setMessage(""); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-update-button"><Plus className="h-3.5 w-3.5 mr-1" /> Add Update</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Customer-visible Update</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="update-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Campaign Started" className="mt-1.5" /></div>
            <div><Label>Message</Label><Textarea data-testid="update-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Your campaign has started successfully." className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-update" className="bg-[#1e3a8a]">Post</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PaymentEditor({ orderId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: "", payment_date: "", payment_method: "", transaction_ref: "", note: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.amount) { toast.error("Amount required"); return; }
    try { await api.post(`/orders/${orderId}/payments`, { ...f, amount: Number(f.amount) }); toast.success("Payment recorded"); setOpen(false); setF({ amount: "", payment_date: "", payment_method: "", transaction_ref: "", note: "" }); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-payment-button"><Plus className="h-3.5 w-3.5 mr-1" /> Record Payment</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount (₹)</Label><Input data-testid="payment-amount" type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Date</Label><Input data-testid="payment-date" type="date" value={f.payment_date} onChange={(e) => set("payment_date", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Method</Label>
              <Select value={f.payment_method} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger className="mt-1.5" data-testid="payment-method"><SelectValue placeholder="Method" /></SelectTrigger>
                <SelectContent>{["Cash", "UPI", "Bank Transfer", "Cheque", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reference</Label><Input data-testid="payment-ref" value={f.transaction_ref} onChange={(e) => set("transaction_ref", e.target.value)} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-payment" className="bg-[#1e3a8a]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FileUploader({ orderId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("document");
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category); fd.append("customer_visible", visible);
    try { await api.post(`/orders/${orderId}/files`, fd); toast.success("File uploaded"); setOpen(false); setFile(null); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="upload-file-button"><Upload className="h-3.5 w-3.5 mr-1" /> Upload</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload File</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="file-input" type="file" onChange={(e) => setFile(e.target.files?.[0])} />
            <div><Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1.5" data-testid="file-category"><SelectValue /></SelectTrigger>
                <SelectContent>{["logo", "creative", "video", "document", "screenshot"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="text-sm font-medium">Visible to Customer</div><div className="text-xs text-slate-500">Show this file in the customer portal</div></div>
              <Switch checked={visible} onCheckedChange={setVisible} data-testid="file-visible-toggle" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="save-file" className="bg-[#1e3a8a]">Upload</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FileRow({ file, isAdmin, onDelete }) {
  const Icon = CAT_ICON[file.category] || FileText;
  const download = async () => {
    try {
      const res = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = file.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  const remove = async () => { await api.delete(`/files/${file.id}`); toast.success("Removed"); onDelete(); };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
      <span className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center text-slate-600"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900 truncate">{file.original_filename}</div>
        <div className="text-xs text-slate-500 capitalize">{file.category} {!file.customer_visible && "• Internal"}</div>
      </div>
      <button onClick={download} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`download-file-${file.id}`}><Download className="h-4 w-4" /></button>
      {isAdmin && <button onClick={remove} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
}
